import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../i18n/LanguageContext';
import styled, { keyframes } from 'styled-components';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const HRPolicyContainer = styled.div`
  padding: 140px 0 60px;
  
  @media (max-width: 768px) {
    padding: 100px 0 40px;
  }
  background: #ffffff;
  min-height: 100vh;
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 5%;
`;

const PageTitle = styled.h1`
  text-align: center;
  font-size: 4.5rem;
  color: #2c2c2c;
  margin-bottom: 30px;
  
  letter-spacing: -2px;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 4px;
    background: linear-gradient(90deg, #0a1628, #1a2744);
    border-radius: 2px;
  }
  
  @media (max-width: 768px) {
    font-size: 3rem;
  }
`;

const PageSubtitle = styled.p`
  text-align: center;
  font-size: 1.4rem;
  color: #6c757d;
  margin-bottom: 120px;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.7;
  
`;

// Hero Section
const HeroSection = styled.section`
  position: relative;
  height: 500px;
  border-radius: 25px;
  overflow: hidden;
  margin-bottom: 120px;
  
  &.animate-in {
    animation: ${scaleIn} 0.8s ease-out;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('https://images.unsplash.com/photo-1504307651254-35680f356dfd?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80') no-repeat center center;
    background-size: cover;
    transition: transform 0.5s ease;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(10, 22, 40, 0.85), rgba(26, 39, 68, 0.7));
  }
  
  &:hover::before {
    transform: scale(1.05);
  }
`;

const HeroContent = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  z-index: 2;
  color: white;
  
  h2 {
    font-size: 3.5rem;
    
    margin-bottom: 25px;
    letter-spacing: -1.5px;
  
    color: #ffffff;}
  
  p {
    font-size: 1.3rem;
    
    line-height: 1.6;
    max-width: 600px;
    margin: 0 auto;
    opacity: 0.95;
  
    color: #ffffff;}
  
  @media (max-width: 768px) {
    h2 {
      font-size: 2.5rem;
    }
    
    p {
      font-size: 1.1rem;
    }
  }
`;

// Policies Grid
const PoliciesSection = styled.section`
  margin-bottom: 120px;
  
  &.animate-in {
    .policies-grid .policy-card:nth-child(1) { animation: ${scaleIn} 0.6s ease-out 0.1s both; }
    .policies-grid .policy-card:nth-child(2) { animation: ${scaleIn} 0.6s ease-out 0.3s both; }
    .policies-grid .policy-card:nth-child(3) { animation: ${scaleIn} 0.6s ease-out 0.5s both; }
    .policies-grid .policy-card:nth-child(4) { animation: ${scaleIn} 0.6s ease-out 0.7s both; }
  }
`;

const SectionTitle = styled.h2`
  font-size: 3.5rem;
  
  color: #2c2c2c;
  margin-bottom: 80px;
  text-align: center;
  letter-spacing: -1.5px;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const PoliciesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 50px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const PolicyCard = styled.div`
  background: white;
  padding: 50px 40px;
  border-radius: 20px;
  border: 1px solid rgba(10, 22, 40, 0.08);
  transition: all 0.4s ease;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: linear-gradient(180deg, #0a1628, #1a2744);
    transform: scaleY(0);
    transition: transform 0.4s ease;
  }
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 40px rgba(10, 22, 40, 0.12);
    border-color: rgba(10, 22, 40, 0.15);
  }
  
  &:hover::before {
    transform: scaleY(1);
  }
  
  .icon {
    font-size: 3.5rem;
    color: #0a1628;
    margin-bottom: 25px;
    transition: all 0.4s ease;
  }
  
  &:hover .icon {
    transform: scale(1.1);
    color: #1a2744;
  }
  
  h3 {
    font-size: 1.8rem;
    color: #2c2c2c;
    margin-bottom: 20px;
    font-weight: 500;
    letter-spacing: -0.5px;
  }
  
  p {
    color: #6c757d;
    line-height: 1.7;
    font-size: 1.1rem;
    
    margin-bottom: 25px;
  }
  
  .features {
    list-style: none;
    padding: 0;
    
    li {
      display: flex;
      align-items: center;
      gap: 10px;
      margin-bottom: 10px;
      color: #6c757d;
      font-size: 1rem;
      
      &::before {
        content: '✓';
        color: #0a1628;
        font-weight: bold;
      }
    }
  }
`;

// Benefits Section
const BenefitsSection = styled.section`
  background: linear-gradient(135deg, #0a1628 0%, #1a2744 100%);
  padding: 100px 0;
  border-radius: 30px;
  text-align: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 80%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.05) 0%, transparent 50%);
    pointer-events: none;
  }
  
  &.animate-in {
    .benefits-grid .benefit-item:nth-child(1) { animation: ${fadeInUp} 0.6s ease-out 0.1s both; }
    .benefits-grid .benefit-item:nth-child(2) { animation: ${fadeInUp} 0.6s ease-out 0.3s both; }
    .benefits-grid .benefit-item:nth-child(3) { animation: ${fadeInUp} 0.6s ease-out 0.5s both; }
    .benefits-grid .benefit-item:nth-child(4) { animation: ${fadeInUp} 0.6s ease-out 0.7s both; }
  }
  
  h2 {
    font-size: 3.5rem;
    
    color: white;
    margin-bottom: 80px;
    letter-spacing: -1.5px;
  }
  
  @media (max-width: 768px) {
    h2 {
      font-size: 2.5rem;
    }
  }
`;

const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 50px;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const BenefitItem = styled.div`
  .icon {
    font-size: 3rem;
    color: rgba(255, 255, 255, 0.9);
    margin-bottom: 20px;
  }
  
  h3 {
    font-size: 1.4rem;
    color: white;
    margin-bottom: 15px;
    font-weight: 500;
  }
  
  p {
    color: rgba(255, 255, 255, 0.8);
    line-height: 1.6;
    
  }
`;

const HRPolicy = () => {
  const [animatedSections, setAnimatedSections] = useState(new Set());
  const { t } = useLanguage();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setAnimatedSections(prev => new Set(prev).add(entry.target.id));
          }
        });
      },
      { threshold: 0.1 }
    );

    const sections = document.querySelectorAll('[data-animate]');
    sections.forEach(section => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  return (
    <HRPolicyContainer>
      <Container>
        <PageTitle>{t('hrPolicy.title')}</PageTitle>
        <PageSubtitle>
          {t('hrPolicy.subtitle')}
        </PageSubtitle>

        <HeroSection 
          id="hero-section" 
          data-animate
          className={animatedSections.has('hero-section') ? 'animate-in' : ''}
        >
          <HeroContent>
            <h2>{t('hrPolicy.heroTitle')}</h2>
            <p>
              {t('hrPolicy.heroDesc')}
            </p>
          </HeroContent>
        </HeroSection>

        <PoliciesSection 
          id="policies-section" 
          data-animate
          className={animatedSections.has('policies-section') ? 'animate-in' : ''}
        >
          <SectionTitle>{t('hrPolicy.policiesTitle')}</SectionTitle>
          <PoliciesGrid className="policies-grid">
            <PolicyCard className="policy-card">
              <div className="icon">👥</div>
              <h3>{t('hrPolicy.pol1Title')}</h3>
              <p>{t('hrPolicy.pol1Desc')}</p>
              <ul className="features">
                <li>{t('hrPolicy.pol1f1')}</li>
                <li>{t('hrPolicy.pol1f2')}</li>
                <li>{t('hrPolicy.pol1f3')}</li>
                <li>{t('hrPolicy.pol1f4')}</li>
              </ul>
            </PolicyCard>

            <PolicyCard className="policy-card">
              <div className="icon">📚</div>
              <h3>{t('hrPolicy.pol2Title')}</h3>
              <p>{t('hrPolicy.pol2Desc')}</p>
              <ul className="features">
                <li>{t('hrPolicy.pol2f1')}</li>
                <li>{t('hrPolicy.pol2f2')}</li>
                <li>{t('hrPolicy.pol2f3')}</li>
                <li>{t('hrPolicy.pol2f4')}</li>
              </ul>
            </PolicyCard>

            <PolicyCard className="policy-card">
              <div className="icon">🛡️</div>
              <h3>{t('hrPolicy.pol3Title')}</h3>
              <p>{t('hrPolicy.pol3Desc')}</p>
              <ul className="features">
                <li>{t('hrPolicy.pol3f1')}</li>
                <li>{t('hrPolicy.pol3f2')}</li>
                <li>{t('hrPolicy.pol3f3')}</li>
                <li>{t('hrPolicy.pol3f4')}</li>
              </ul>
            </PolicyCard>

            <PolicyCard className="policy-card">
              <div className="icon">⚖️</div>
              <h3>{t('hrPolicy.pol4Title')}</h3>
              <p>{t('hrPolicy.pol4Desc')}</p>
              <ul className="features">
                <li>{t('hrPolicy.pol4f1')}</li>
                <li>{t('hrPolicy.pol4f2')}</li>
                <li>{t('hrPolicy.pol4f3')}</li>
                <li>{t('hrPolicy.pol4f4')}</li>
              </ul>
            </PolicyCard>
          </PoliciesGrid>
        </PoliciesSection>

        <BenefitsSection 
          id="benefits-section" 
          data-animate
          className={animatedSections.has('benefits-section') ? 'animate-in' : ''}
        >
          <h2>{t('hrPolicy.benefitsTitle')}</h2>
          <BenefitsGrid className="benefits-grid">
            <BenefitItem className="benefit-item">
              <div className="icon">💰</div>
              <h3>{t('hrPolicy.ben1Title')}</h3>
              <p>{t('hrPolicy.ben1Desc')}</p>
            </BenefitItem>

            <BenefitItem className="benefit-item">
              <div className="icon">🏥</div>
              <h3>{t('hrPolicy.ben2Title')}</h3>
              <p>{t('hrPolicy.ben2Desc')}</p>
            </BenefitItem>

            <BenefitItem className="benefit-item">
              <div className="icon">🎓</div>
              <h3>{t('hrPolicy.ben3Title')}</h3>
              <p>{t('hrPolicy.ben3Desc')}</p>
            </BenefitItem>

            <BenefitItem className="benefit-item">
              <div className="icon">🏖️</div>
              <h3>{t('hrPolicy.ben4Title')}</h3>
              <p>{t('hrPolicy.ben4Desc')}</p>
            </BenefitItem>
          </BenefitsGrid>
        </BenefitsSection>
      </Container>
    </HRPolicyContainer>
  );
};

export default HRPolicy;